import { useMemo, useState } from "react";
import { addDays, addMonths, endOfMonth, format, isSameMonth, isToday, startOfMonth, startOfWeek } from "date-fns";
import { ChevronLeft, ChevronRight, Users, Home } from "lucide-react";

const iso = (d) => format(d, "yyyy-MM-dd");

function buildWeeks(month) {
  const start = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
  const end = endOfMonth(month);
  const weeks = [];
  for (let d = start; d <= end || weeks.length < 5; d = addDays(d, 7)) {
    weeks.push(Array.from({ length: 7 }, (_, i) => addDays(d, i)));
    if (weeks.length === 6) break;
  }
  return weeks;
}

function Chip({ r }) {
  const tone = r.subject_type === "client" ? "bg-slateblue-light text-slateblue" : r.kind === "sickness" ? "bg-coral-light text-coral-dark" : "bg-brand-light text-brand";
  return <span title={`${r.subject_name} · ${r.subject_type === "client" ? "client holiday" : r.kind === "sickness" ? "sickness" : "staff leave"}`} className={`block truncate rounded px-1.5 py-0.5 text-[10px] font-semibold ${tone}`}>{r.subject_name}</span>;
}

function Day({ day, month, items, carerIds }) {
  const staffOff = new Set(items.filter((r) => r.subject_type === "staff" && carerIds.has(r.subject_id)).map((r) => r.subject_id)).size;
  const staffTotal = carerIds.size;
  const busy = staffTotal > 0 && staffOff * 3 >= staffTotal;
  return (
    <div data-testid={`leave-cal-day-${iso(day)}`} className={`min-h-[86px] border-b border-r border-border p-1.5 ${isSameMonth(day, month) ? "bg-white" : "bg-ivory/60 text-ink-muted"} ${busy ? "ring-2 ring-inset ring-coral/60" : ""}`}>
      <div className="mb-1 flex items-center justify-between">
        <span className={`text-xs font-semibold ${isToday(day) ? "rounded-full bg-brand px-1.5 text-white" : ""}`}>{format(day, "d")}</span>
        {staffOff > 0 && <span className={`text-[10px] font-semibold ${busy ? "text-coral-dark" : "text-ink-muted"}`} data-testid={`leave-cal-off-${iso(day)}`}>{staffOff} befriender{staffOff === 1 ? "" : "s"} off</span>}
      </div>
      <div className="space-y-0.5">
        {items.slice(0, 3).map((r) => <Chip key={r.id} r={r} />)}
        {items.length > 3 && <span className="block text-[10px] text-ink-muted">+{items.length - 3} more</span>}
      </div>
    </div>
  );
}

export function LeaveCalendar({ rows, carerIds }) {
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const approved = useMemo(() => rows.filter((r) => r.status === "approved" && r.start_date && r.end_date), [rows]);
  const weeks = buildWeeks(month);
  const on = (d) => { const k = iso(d); return approved.filter((r) => r.start_date <= k && k <= r.end_date); };
  const monthStart = iso(startOfMonth(month));
  const monthEnd = iso(endOfMonth(month));
  const inMonth = approved.filter((r) => r.start_date <= monthEnd && r.end_date >= monthStart);
  return (
    <div className="rounded-xl border border-border bg-white shadow-sm" data-testid="leave-calendar">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border p-4">
        <div className="flex items-center gap-2">
          <button onClick={() => setMonth(addMonths(month, -1))} data-testid="leave-cal-prev" className="rounded-lg border border-border p-1.5 hover:bg-secondary"><ChevronLeft size={16} /></button>
          <h3 className="w-40 text-center font-heading font-semibold text-ink" data-testid="leave-cal-month">{format(month, "MMMM yyyy")}</h3>
          <button onClick={() => setMonth(addMonths(month, 1))} data-testid="leave-cal-next" className="rounded-lg border border-border p-1.5 hover:bg-secondary"><ChevronRight size={16} /></button>
          <button onClick={() => setMonth(startOfMonth(new Date()))} className="ml-1 rounded-lg px-2 py-1 text-xs font-semibold text-brand hover:bg-brand-light">Today</button>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-[11px] text-ink-soft">
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded bg-brand-light" /> Staff leave</span>
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded bg-coral-light" /> Sickness</span>
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded bg-slateblue-light" /> Client holiday</span>
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded ring-2 ring-coral/60" /> A third or more of befrienders off</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-[640px]">
          <div className="grid grid-cols-7 border-b border-border bg-secondary/50 text-center text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="py-2">{d}</div>)}
          </div>
          {weeks.map((w) => <div key={iso(w[0])} className="grid grid-cols-7 border-l border-border">{w.map((d) => <Day key={iso(d)} day={d} month={month} items={on(d)} carerIds={carerIds} />)}</div>)}
        </div>
      </div>
      <div className="grid gap-2 p-4 text-sm sm:grid-cols-2" data-testid="leave-cal-summary">
        <p className="flex items-center gap-2 text-ink-soft"><Users size={15} className="text-brand" /> {inMonth.filter((r) => r.subject_type === "staff").length} staff absences this month</p>
        <p className="flex items-center gap-2 text-ink-soft"><Home size={15} className="text-slateblue" /> {inMonth.filter((r) => r.subject_type === "client").length} client holidays this month (visits paused)</p>
      </div>
    </div>
  );
}
